import React, { useMemo, useState } from "react";

import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Trans } from "react-i18next";
import { NavLink } from "react-router-dom";

import { models } from "@/data/models";
import { getUrlLangPrefix } from "@/utils/Local";
import { formatMoney } from "@/utils/Toolbox";

import FaArea from "../assets/icons/faArea.svg";
import FaBed from "../assets/icons/faBed.svg";
import FaBottle from "../assets/icons/faBottle.svg";
import BannerComponent from "../components/Banner.component";
import ContainerDivider from "../components/ContainerDivider.component";
import DocumentComponent from "../components/Document.component";
import PremiumUpgradesComponent from "../components/PremiumUpgrades.component";
import ScheduleComponent from "../components/Schedule.component";
import i18n from "../i18n";

import type { IModel } from "@/data/models";

type SortType = "price-asc" | "price-desc" | "popular";

// Attach original index for relevance sorting
const modelsWithIndex = models.map((item, index) => ({
  ...item,
  originalIndex: index,
}));

const renderModels = (modelsList: IModel[], langPrefix: string, t: any) => {
  const bedroomLabels: Record<number, string> = {
    1: t("One"),
    2: t("Two"),
    3: t("Three"),
  };
  return modelsList.map((model: IModel, index: number) => (
    <Col key={index} className="mb-5 mb-lg-4" lg={6}>
      <Card className="p-0 h-100">
        <NavLink
          className="text-center text-lg-end"
          to={`${langPrefix}/${t("_route.models")}/${model.slug}`}
        >
          <Card.Img
            alt={model.name}
            className="modelCardImg"
            src={model.img}
            variant="top"
          />
        </NavLink>

        <Card.Body className="modelsSummaryContainer">
          <Card.Title className="m-0">
            <h2 className="modelHeader text-start m-0">{t(model.name)}</h2>
          </Card.Title>

          <Card.Text className="modelHeaderPricing mb-0">
            <span>
              {t("starting_from_price", { price: formatMoney(model.price) })}
            </span>
          </Card.Text>

          <div className="d-flex flex-lg-column justify-content-between">
            <Card.Text className="modelItem equal">
              <img alt={t("recycled_water_bottle")} src={FaBottle} />
              <span className="d-block d-lg-none">
                {model.bottles.toLocaleString()}
              </span>
              <span className="d-none d-lg-block">
                {t("made_from_x_recycled_water_bottles", {
                  x: model.bottles.toLocaleString(),
                })}
              </span>
            </Card.Text>
            {model.bedrooms.length > 0 && (
              <Card.Text className="modelItem equal">
                <img alt={t(`model_bedrooms`)} src={FaBed} />
                <span className="d-block d-lg-none">
                  {model.bedrooms.filter(Boolean).join(` ${t("or")} `)}
                </span>
                <span className="d-none d-lg-block">
                  {model.bedrooms
                    .map((bedroom) => bedroomLabels[bedroom] ?? "")
                    .filter(Boolean)
                    .join(` ${t("or")} `)}
                  &nbsp;{t("bedroom house")}
                </span>
              </Card.Text>
            )}
            <Card.Text
              className={`modelItem ${model.bedrooms.length > 0 ? "equal" : ""}`}
            >
              <img alt={t(`model_area`)} src={FaArea} />
              <span>
                {model.areas.length > 1 ? (
                  <React.Fragment>
                    {model.areas.length === 2 ? (
                      <React.Fragment>
                        <span className="d-none d-lg-block">
                          {model.areas.map((area, index) => {
                            const separator =
                              index !== 0 ? "" : (t("to") as string);
                            return (
                              <span key={index}>
                                {area}&nbsp;{t("sq_ft")}&nbsp;{separator}
                                &nbsp;
                              </span>
                            );
                          })}
                        </span>

                        <span className="d-block d-lg-none">{`${model.areas[0]} ${t("sq_ft")}`}</span>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <span className="float-start me-1">{`${model.areas[0]} ${t("sq_ft")}`}</span>
                        <span className="d-none d-lg-block float-start">
                          ({t("also available in")}&nbsp;
                          {model.areas.map((area, index) => {
                            if (index === 0) {
                              return null;
                            }

                            const separator =
                              index !== model.areas.length - 1 ? ", " : "";
                            return (
                              <span key={index}>
                                {area}&nbsp;{t("sq_ft")}
                                {separator}
                              </span>
                            );
                          })}
                          )
                        </span>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>{`${model.areas[0]} ${t("sq_ft")}`}</React.Fragment>
                )}
              </span>
            </Card.Text>
          </div>
        </Card.Body>

        <Card.Footer
          className="mb-2"
          style={{ background: "unset", border: "unset" }}
        >
          <NavLink
            className="text-center text-lg-end modelLink"
            to={`${langPrefix}/${t("_route.models")}/${model.slug}`}
          >
            <span>
              {t("learn_more")} <FontAwesomeIcon icon={faChevronRight} />
            </span>
          </NavLink>
        </Card.Footer>
      </Card>
    </Col>
  ));
};

const Models = () => {
  const { t } = i18n;
  const langPrefix = getUrlLangPrefix();
  const [showHiddenModels, setShowHiddenModels] = useState(false);
  const [sortType, setSortType] = useState<SortType>("popular");

  const sortList = [
    {
      label: t("Popular"),
      value: "popular",
    },
    {
      label: t("Price (high to low)"),
      value: "price-desc",
    },
    {
      label: t("Price (low to high)"),
      value: "price-asc",
    },
  ];

  const sortedModels = useMemo(() => {
    const arr = [...modelsWithIndex];

    switch (sortType) {
      case "price-asc":
        return arr.sort((a, b) => a.price - b.price);
      case "price-desc":
        return arr.sort((a, b) => b.price - a.price);
      case "popular":
        return arr.sort((a, b) => a.originalIndex - b.originalIndex);
      default:
        return arr;
    }
  }, [sortType]);

  const visibleModels = useMemo(() => sortedModels.slice(0, 4), [sortedModels]);
  const hiddenModels = useMemo(
    () => sortedModels.slice(4, sortedModels.length),
    [sortedModels],
  );

  const handleSortSelect = (sort: SortType) => {
    setSortType(sort);
    setShowHiddenModels(false);
  };

  const signatureModelsDescription = (
    <Trans i18nKey="signature_models_description">
      <NavLink to={`${langPrefix}/${t("_route.contact-us")}`}>
        ${t("contact us")}
      </NavLink>
    </Trans>
  );

  const btnLabel = showHiddenModels ? t("Show Less") : t("Show More");
  const handleShowBtnClick = (): void => {
    if (showHiddenModels && window) {
      window.scrollTo(0, 0);
    }
    setShowHiddenModels(!showHiddenModels);
  };

  return (
    <React.Fragment>
      <DocumentComponent title={t("signature_models")} />

      <BannerComponent
        description={signatureModelsDescription}
        title={t("signature_models")}
      />

      <Container style={{ marginBottom: "5rem" }}>
        <Row>
          <Col className="mb-4 modelSortContainer" xs={12}>
            <Form.Select
              id="model-sort"
              value={sortType}
              onChange={(e) => {
                handleSortSelect(e.target.value as SortType);
              }}
            >
              {sortList.map((item, i) => (
                <option key={i} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row>
          {renderModels(visibleModels, langPrefix, t)}
          {showHiddenModels ? renderModels(hiddenModels, langPrefix, t) : null}
        </Row>

        <Row>
          <Col className="text-center mt-4" xs={12}>
            <Button
              className="btn-outline"
              onClick={() => handleShowBtnClick()}
              size="sm"
              variant="outline-success ps-4 pe-4"
            >
              {btnLabel}
            </Button>
          </Col>
        </Row>
      </Container>

      <ContainerDivider title={t("premium_upgrades")} />

      <PremiumUpgradesComponent />

      <ScheduleComponent />
    </React.Fragment>
  );
};

export default Models;
